// Securely read file into memory before passing to AudioContext
const readAsArrayBufferAsync = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error(`Failed to read file: ${file.name}`));
    reader.readAsArrayBuffer(file);
  });
};

// Safely decode audio natively
const decodeAudioDataSafe = (ctx, arrayBuffer) => {
  return new Promise((resolve, reject) => {
    ctx.decodeAudioData(
      arrayBuffer,
      (buffer) => resolve(buffer),
      (err) => reject(new Error(err ? err.message : "Unsupported audio format or corrupted file."))
    );
  });
};

// Native WAV Encoder - No external libraries needed!
const audioBufferToWav = async (buffer, onProgress) => {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const format = 1; // PCM
  const bitDepth = 16;
  
  const channelData = buffer.getChannelData(0); // We rendered in Mono
  const bufferLength = channelData.length;
  
  const arrayBuffer = new ArrayBuffer(44 + bufferLength * 2);
  const view = new DataView(arrayBuffer);

  const writeString = (view, offset, string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };

  // Write standard WAV Header
  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + bufferLength * 2, true);
  writeString(view, 8, 'WAVE');
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, format, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * 2, true);
  view.setUint16(32, numChannels * 2, true);
  view.setUint16(34, bitDepth, true);
  writeString(view, 36, 'data');
  view.setUint32(40, bufferLength * 2, true);

  // Write PCM audio samples
  let offset = 44;
  for (let i = 0; i < bufferLength; i++) {
    let sample = Math.max(-1, Math.min(1, channelData[i]));
    sample = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
    view.setInt16(offset, sample, true);
    offset += 2;
    
    // Progress update every ~100k samples to keep UI smooth
    if (i % 100000 === 0 && onProgress) {
      const progress = Math.round((i / bufferLength) * 100);
      onProgress(`Compressing... ${progress}%`);
      await new Promise(r => setTimeout(r, 0)); // Yield to main thread
    }
  }

  return new Blob([view], { type: 'audio/wav' });
};

export const mixAudioFiles = async (file1, file2, crossfadeSeconds = 3, onProgress) => {
  let ctx;
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) throw new Error("Web Audio API is not supported in this browser.");
    
    ctx = new AudioContextClass(); 

    onProgress("Reading Track 1...");
    const arrayBuffer1 = await readAsArrayBufferAsync(file1);
    onProgress("Decoding Track 1...");
    const audioBuffer1 = await decodeAudioDataSafe(ctx, arrayBuffer1);

    onProgress("Reading Track 2...");
    const arrayBuffer2 = await readAsArrayBufferAsync(file2);
    onProgress("Decoding Track 2...");
    const audioBuffer2 = await decodeAudioDataSafe(ctx, arrayBuffer2);

    if (!audioBuffer1.duration || !audioBuffer2.duration) {
      throw new Error("One of the audio tracks has an invalid duration.");
    }

    const maxSafeCrossfade = Math.min(audioBuffer1.duration * 0.4, audioBuffer2.duration * 0.4);
    const actualCrossfade = Math.max(0.01, Math.min(crossfadeSeconds, maxSafeCrossfade));
    const totalDuration = audioBuffer1.duration + audioBuffer2.duration - actualCrossfade;
    
    // --- DYNAMIC COMPRESSION LOGIC ---
    // Gemini limit is ~12MB. We aim for 10MB max to be completely safe.
    // 10MB = 10,485,760 bytes.
    // Bytes = Duration * SampleRate * Channels(1 Mono) * BytesPerSample(2)
    // Dynamic SampleRate = Bytes / (Duration * 2)
    const maxSafeBytes = 10 * 1024 * 1024;
    let targetSampleRate = Math.floor(maxSafeBytes / (totalDuration * 2));
    
    // Cap sample rate: 8000Hz (minimum for AI voice logic) up to 22050Hz (good quality)
    targetSampleRate = Math.max(8000, Math.min(22050, targetSampleRate));
    
    const OfflineContextClass = window.OfflineAudioContext || window.webkitOfflineAudioContext;
    if (!OfflineContextClass) throw new Error("Offline Audio Routing is not supported.");
    
    // Create Context: 1 Channel (Mono) + Dynamic Sample Rate to ensure file is ALWAYS small
    const offlineCtx = new OfflineContextClass(1, Math.ceil(targetSampleRate * totalDuration), targetSampleRate);

    onProgress(`Mixing audio layers...`);

    // Setup Track 1
    const source1 = offlineCtx.createBufferSource();
    source1.buffer = audioBuffer1;
    const gain1 = offlineCtx.createGain();
    source1.connect(gain1);
    gain1.connect(offlineCtx.destination);

    // Setup Track 2
    const source2 = offlineCtx.createBufferSource();
    source2.buffer = audioBuffer2;
    const gain2 = offlineCtx.createGain();
    source2.connect(gain2);
    gain2.connect(offlineCtx.destination);

    // Crossfade Automation
    const track2StartTime = Math.max(0, audioBuffer1.duration - actualCrossfade);

    // Track 1 Fade Out
    gain1.gain.setValueAtTime(1, 0);
    gain1.gain.setValueAtTime(1, Math.max(0, audioBuffer1.duration - actualCrossfade));
    gain1.gain.linearRampToValueAtTime(0, audioBuffer1.duration);

    // Track 2 Fade In
    gain2.gain.setValueAtTime(0, 0);
    if (track2StartTime > 0) gain2.gain.setValueAtTime(0, track2StartTime);
    gain2.gain.linearRampToValueAtTime(1, track2StartTime + actualCrossfade);

    source1.start(0);
    source2.start(track2StartTime);

    const renderedBuffer = await offlineCtx.startRendering();

    // Encode to WAV natively
    const wavBlob = await audioBufferToWav(renderedBuffer, onProgress);

    const name1 = file1.name.replace(/\.[^/.]+$/, "");
    const name2 = file2.name.replace(/\.[^/.]+$/, "");
    
    const mixedFile = new File([wavBlob], `Mixed_${name1}_${name2}.wav`, { type: 'audio/wav' });
    
    if (ctx.state !== 'closed') ctx.close();

    return mixedFile;

  } catch (error) {
    console.error("Detailed Mixer Error:", error);
    if (ctx && ctx.state !== 'closed') {
      ctx.close().catch(() => console.log('Cleanup ignored'));
    }
    throw new Error(`${error.message}`);
  }
};